import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { detectDevice, Device } from './lib/device';
import { applyInitialTheme, ConfirmProvider, ToastProvider, useConfirm, useToast } from './lib/ui';
import { setGlobalConfirm, setSessionHandler } from './api/client';
import { useAuth } from './hooks/useAuth';
import { authPwChange } from './api/endpoints';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Postes from './pages/Postes';
import Mesures from './pages/Mesures';
import { More, Settings } from './pages/Stubs';
import Jobs from './pages/Jobs';
import Ingest from './pages/Ingest';
import Json from './pages/Json';
import Mqtt from './pages/Mqtt';
import Annotations from './pages/Annotations';
import UpdWeewx from './pages/UpdWeewx';
import Incidents from './pages/Incidents';
import Audit from './pages/Audit';
import Users from './pages/Users';
import Db from './pages/Db';
import DbDetail from './pages/DbDetail';

function useDevice(): Device {
  const [device, setDevice] = useState<Device>(detectDevice());
  useEffect(() => {
    const onResize = () => setDevice(detectDevice());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return device;
}

function Protected({ children }: { children: React.ReactElement }) {
  const { isLoggedIn, isLoading } = useAuth();
  const loc = useLocation();
  if (isLoading) return <div className="p-8 text-muted-foreground">Chargement…</div>;
  if (!isLoggedIn) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function PasswordChange() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => authPwChange({ current, new: next }),
    onSuccess: async () => {
      toast.push('Mot de passe changé', 'success');
      await qc.invalidateQueries({ queryKey: ['me'] });
      nav('/dashboard');
    },
    onError: () => { toast.push('Erreur — vérifiez le mot de passe actuel', 'error'); },
  });
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <form onSubmit={(e) => { e.preventDefault(); m.mutate(); }}
            className="w-full max-w-sm bg-card border border-border rounded-lg p-6 space-y-4 shadow-lg">
        <h1 className="text-xl font-bold">Changer le mot de passe</h1>
        <p className="text-sm text-muted-foreground">Votre compte nécessite un nouveau mot de passe.</p>
        <label className="block text-sm">
          Mot de passe actuel
          <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
                 className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm" />
        </label>
        <label className="block text-sm">
          Nouveau mot de passe
          <input type="password" required minLength={12} value={next} onChange={(e) => setNext(e.target.value)}
                 className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm" />
        </label>
        <button type="submit" disabled={m.isPending}
                className="w-full h-10 rounded-md bg-primary text-primary-foreground font-medium disabled:opacity-50">
          {m.isPending ? 'Enregistrement…' : 'Changer'}
        </button>
      </form>
    </div>
  );
}

function SessionWire() {
  const confirm = useConfirm();
  const toast = useToast();
  const nav = useNavigate();
  useEffect(() => {
    setGlobalConfirm(async (opts) => confirm({ title: opts.title, message: opts.message, destructive: opts.destructive }));
    setSessionHandler({
      on401: () => { nav('/login'); },
      onMustChangePassword: () => { nav('/password-change'); },
      onLocked: (s) => { toast.push(`Trop de tentatives — réessaie dans ${s}s`, 'error'); },
      onError: (m) => { toast.push(m, 'error'); },
    });
  }, [confirm, toast, nav]);
  return null;
}

export default function App() {
  const device = useDevice();
  useEffect(() => { applyInitialTheme(); }, []);

  return (
    <ToastProvider>
      <ConfirmProvider>
        <SessionWire />
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<Login device={device} />} />
          <Route path="/password-change" element={<Protected><PasswordChange /></Protected>} />
          <Route path="/dashboard" element={<Protected><Dashboard device={device} /></Protected>} />
          <Route path="/postes" element={<Protected><Postes device={device} /></Protected>} />
          <Route path="/mesures" element={<Protected><Mesures device={device} /></Protected>} />
          <Route path="/db" element={<Protected><Db device={device} /></Protected>} />
          <Route path="/db/:schema/:name" element={<Protected><DbDetail device={device} /></Protected>} />
          <Route path="/tables" element={<Navigate to="/db" replace />} />
          <Route path="/jobs" element={<Protected><Jobs device={device} /></Protected>} />
          <Route path="/ingest" element={<Protected><Ingest device={device} /></Protected>} />
          <Route path="/json" element={<Protected><Json device={device} /></Protected>} />
          <Route path="/mqtt" element={<Protected><Mqtt device={device} /></Protected>} />
          <Route path="/annotations" element={<Protected><Annotations device={device} /></Protected>} />
          <Route path="/upd-weewx" element={<Protected><UpdWeewx device={device} /></Protected>} />
          <Route path="/incidents" element={<Protected><Incidents device={device} /></Protected>} />
          <Route path="/audit" element={<Protected><Audit device={device} /></Protected>} />
          <Route path="/users" element={<Protected><Users device={device} /></Protected>} />
          <Route path="/settings" element={<Protected><Settings device={device} /></Protected>} />
          <Route path="/more" element={<Protected><More device={device} /></Protected>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </ConfirmProvider>
    </ToastProvider>
  );
}
